import { FC, Fragment } from 'react';
import { kebabCase } from 'lodash';
import clsx from 'clsx';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';

import { Link } from '@/components/link';
import { Icon } from '@/components/icon';
import type { Product, ProductFeature } from '..';

const TooltipTrigger: FC<{ feature: ProductFeature }> = ({ feature }) => {
  const hasDescription = Boolean(feature.description);

  if (!hasDescription) {
    return null;
  }

  return (
    <OverlayTrigger
      rootClose
      trigger="click"
      placement="top"
      overlay={
        <Popover className="popover" id={feature.title}>
          <Popover.Title>{feature.title}</Popover.Title>
          <Popover.Content>
            {feature.description}
            {feature.link && feature.link.url && <Link href={feature.link.url}>{feature.link.label}</Link>}
          </Popover.Content>
        </Popover>
      }
    >
      <button type="button" className="ct-more-info-trigger" aria-label="What does this mean?">
        <Icon name="question-circle" />
      </button>
    </OverlayTrigger>
  );
};

const Feature: FC<{ feature: ProductFeature; products: Array<Product> }> = ({ feature, products }) => {
  return (
    <Fragment key={kebabCase(feature.title)}>
      <tr className="ct-label-row" aria-hidden>
        <th colSpan={products.length + 1}>
          <span>{feature.title}</span>
          <TooltipTrigger feature={feature} />
        </th>
      </tr>

      <tr>
        <th className="ct-label-cell">
          <span>{feature.title}</span>
          <TooltipTrigger feature={feature} />
          <p className="ct-label-subtitle">{feature.subtitle}</p>
        </th>
        {products.map((product) => {
          const productFeature = product.features.find(({ type }) => type === feature.type);

          return (
            <td key={product.title} className={clsx({ 'is-recommended': product.isFeatured })}>
              <Icon
                name={productFeature?.isIncluded ? 'check' : 'times'}
                colour={productFeature?.isIncluded ? 'Green' : null}
              />
              <span className="sr-only">{productFeature?.isIncluded ? 'Yes' : 'No'}</span>
              {productFeature?.conditions && <span className="ct-conditions">{productFeature?.conditions}</span>}
            </td>
          );
        })}
      </tr>
    </Fragment>
  );
};

export { Feature };
